import Button from '@codegouvfr/react-dsfr/Button';
import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import Table from '@codegouvfr/react-dsfr/Table';
import { ContextLabels } from 'maestro-shared/schema/ProgrammingPlan/Context';
import {
  isCreatedPartialSample,
  isDeletableSample,
  type PartialSample,
  type PartialSampleToCreate
} from 'maestro-shared/schema/Sample/Sample';
import { getSampleMatrixLabels } from 'maestro-shared/schema/Sample/SampleMatrix';
import { useMemo } from 'react';
import { SampleStatusBadge } from 'src/components/SampleStatusBadge/SampleStatusBadge';
import RemoveSample from 'src/components/SampleTable/RemoveSample';
import { useAuthentication } from 'src/hooks/useAuthentication';
import { useMatrixReferential } from 'src/hooks/useMatrixReferential';
import { useSamplesLink } from 'src/hooks/useSamplesLink';
import './SampleTable.scss';
import { formatMaestroDate } from 'maestro-shared/utils/date';

interface Props {
  samples: (PartialSample | PartialSampleToCreate)[];
  tableFooter?: React.ReactNode;
}

const SampleTable = ({ samples, tableFooter }: Props) => {
  const { sampleLink, navigateToSample } = useSamplesLink();

  const { hasUserPermission, user } = useAuthentication();
  const matrixReferential = useMatrixReferential();

  const tableHeaders = [
    '#',
    'Matrice',
    'Préleveur',
    'Date',
    'Dépt.',
    'Entité',
    'Contexte',
    'Statut',
    'Actions'
  ];

  const tableData = useMemo(
    () =>
      (samples ?? []).map((sample) => [
        ...[
          isCreatedPartialSample(sample) ? sample.reference : '',
          getSampleMatrixLabels(matrixReferential, sample.matrices).join(', '),
          <div className="d-flex-align-center" key={`${sample.id}-name`}>
            {isCreatedPartialSample(sample) ? sample.sampler.name : user?.name}
          </div>,
          formatMaestroDate(sample.sampledDate),
          sample.department,
          sample.company?.name ?? '',
          sample.context ? ContextLabels[sample.context] : '',
          <SampleStatusBadge sample={sample} key={`${sample.id}-badge`} />
        ].map((cell, index) => (
          <div
            key={`${sample.id}-cell-${index}`}
            onClick={() => navigateToSample(sample.id)}
            style={{
              cursor: 'pointer',
              ...(index === 0 ? { whiteSpace: 'nowrap' } : {})
            }}
          >
            {cell}
          </div>
        )),
        <div className="actions" key={`${sample.id}-actions`}>
          <Button
            title="Voir le prélèvement"
            iconId={'fr-icon-eye-fill'}
            linkProps={{
              to: sampleLink(sample.id)
            }}
            size="small"
            priority="tertiary"
          />
          {hasUserPermission('deleteSample') && isDeletableSample(sample) && (
            <RemoveSample sample={sample} />
          )}
        </div>
      ]),
    [samples, matrixReferential]
  );

  return (
    <>
      <Table
        noCaption
        fixed={false}
        headers={tableHeaders}
        data={tableData}
        className={cx('fr-mb-2w')}
      />
      {tableFooter}
    </>
  );
};

export default SampleTable;
